import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import {
  Box,
  Divider,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  VStack,
  useDisclosure,
  useTheme,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useFigmaDownload } from 'hooks/useFigmaDownload';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from 'auth/AuthProvider';
import { useCurrentUser } from 'auth/CurrentUserProvider';
import Button from 'components/Button';
import CardIcon from 'components/CardIcon';
import DevSandbox from 'components/DevSandbox';
import { formatCode } from 'components/DevSandbox/CodeEditor/CodeEditor';
import EditorSettingsButton, { ShareButton } from 'components/DevSandbox/EditorSettingsButton';
import { MerlinToggleButton } from 'components/DevSandbox/Merlin';
import BrowserTab from 'components/DevSandbox/PaneTabs/BrowserTab';
import DiscussionTab from 'components/DevSandbox/PaneTabs/Discussion/DiscussionTab';
import RightSideActions from 'components/DevSandbox/RightSideActions';
import { HTML_LEARN_TEMPLATE } from 'components/DevSandbox/templates';
import Icon from 'components/Icon';
import { UnlockFigmaModal } from 'components/Modal/UnlockFigmaModal';
import ProjectElement from 'components/ProjectElement';
import { ProtectedElement } from 'components/ProtectedElement';
import Tags from 'components/Tags';
import Text from 'components/Text';
import {
  DifficultyMarker,
  StatusIconMapper,
  StatusTextMapper,
} from 'features/practice-problems/PracticeProblems';
import { useMediaQueries } from 'layout/MediaQueriesProvider';
import {
  Support_Type_Enum,
  User_Card_Status_Enum,
  User_Project_Status_Enum,
} from 'types/databaseEnums';
import { MerlinCardInfo, Project, ProjectCard } from 'types/edgeFunctions';
import { ERoute, TanstackQueryName } from 'types/frontend';
import { mapExpToProject } from 'utils/constants';
import { hasFirstCardAccessOnly } from 'utils/user';

import CardDrawer from './CardDrawer';
import CompleteCardCheckbox from './CompleteCardCheckbox';
import ProjectsDrawer from './ProjectsDrawer';

interface IProjectPlayground {
  project: Project;
}

const ProjectPlayground = ({ project }: IProjectPlayground) => {
  const [codeFrameUrl, setCodeFrameUrl] = useState<string>('');
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const loadTimeoutRef = useRef<any | null>(null);

  const { id: userId, isFree, createdAt } = useCurrentUser();
  const { userId: authId } = useAuth();
  const [searchParams] = useSearchParams();
  const { isSmallScreen } = useMediaQueries();

  const [currentCard, setCurrentCard] = useState<ProjectCard | null>(null);

  const { isDownloadingFigmaDesign, onFigmaDownload } = useFigmaDownload({
    screenName: project.screen_name,
    type: 'project',
    entityId: project.id,
  });

  useEffect(() => {
    loadTimeoutRef.current = setTimeout(() => {
      setIsLoaded(true);
    }, 500);

    return () => {
      clearTimeout(loadTimeoutRef.current);
    };
  }, []);

  const handleCardClick = (card: ProjectCard) => {
    setCurrentCard(card);
    setTimeout(() => {
      cardDrawer.onOpen();
    }, 50);
  };

  const codeEditorRef = useRef<any>(null);

  const navigate = useNavigate();

  const figmaModal = useDisclosure();
  const cardDrawer = useDisclosure();

  const { borderRadius } = useTheme();

  const queryClient = useQueryClient();

  const hasTabInUrl =
    location.pathname.includes('browser') ||
    location.pathname.includes('cards') ||
    location.pathname.includes('discussions') ||
    location.pathname.includes('solutions');
  const tabScreenName = location.pathname.split('/').pop();
  const browserUrl = hasTabInUrl
    ? window.location.href.replace(`/${tabScreenName}`, '')
    : window.location.href;

  const searchParamsMode = searchParams.get('mode');
  const mode = searchParamsMode === 'full-page' ? 'full-page' : 'interactive';

  const completedCardsCount =
    project.cards.filter((card) => card.status === User_Card_Status_Enum.Completed).length || 0;

  const currentActiveCard = useMemo(() => {
    const firstIncomplete = project.cards.find(
      (card) => card.status !== User_Card_Status_Enum.Completed
    );
    if (!firstIncomplete) return null;
    return {
      id: firstIncomplete.id,
      title: firstIncomplete.title,
      description: firstIncomplete.description,
      screenshot_url: firstIncomplete.screenshot_url,
      status: firstIncomplete.status,
      index: project.cards.indexOf(firstIncomplete),
    } as MerlinCardInfo;
  }, [project.cards]);

  const merlinCards: MerlinCardInfo[] = useMemo(
    () =>
      project.cards.map((c, i) => ({
        id: c.id,
        title: c.title,
        description: c.description,
        screenshot_url: c.screenshot_url,
        status: c.status,
        index: i,
      })),
    [project.cards]
  );

  const isFreeWithoutCards = !authId || (isFree && hasFirstCardAccessOnly(createdAt));

  return (
    <>
      <DevSandbox
        ref={codeEditorRef}
        id={`project-${project.screen_name}-${userId}`}
        oldKey={project.preview_url}
        browserUrl={browserUrl}
        title={project.title}
        mode={mode}
        entityType="project"
        entityId={project.screen_name}
        merlinConfig={{
          entityName: project.title,
          entityType: 'project',
          entityDescription: project.description,
          designScreenshotUrl: project.design_screenshot.url,
          taskCards: project.cards.map((c) => c.title),
          currentCard: currentActiveCard,
          cards: merlinCards,
          projectScreenName: project.screen_name,
          userProjectId: project.user_project_id,
        }}
        navigation={{
          leftSide: <ProjectsDrawer />,
          header: (
            <HStack spacing={2}>
              <MerlinToggleButton />
              <HStack
                data-tour="submit-button"
                spacing={0}
                borderRadius="md"
                background="linear-gradient(90deg, rgba(159, 122, 234, 0.87) 0%, rgba(237, 137, 54, 0.87) 100%)"
              >
                <ProtectedElement
                  scheme="ghost"
                  leftIcon={<Icon type="SubmitCode" />}
                  onClick={() => {
                    navigate('submit');
                  }}
                  sx={{
                    borderRightRadius: 0,
                    color: 'white',
                    _hover: { bg: 'whiteAlpha.200' },
                  }}
                >
                  Submit
                </ProtectedElement>
              <Menu placement="bottom-end">
                <MenuButton
                  as={IconButton}
                  icon={<ChevronDownIcon />}
                  size="md"
                  aria-label="Submit options"
                  variant="ghost"
                  color="white"
                  sx={{
                    borderLeftRadius: 0,
                    borderLeft: '1px solid',
                    borderColor: 'whiteAlpha.300',
                    minW: '36px',
                    _hover: { bg: 'whiteAlpha.200' },
                    _active: { bg: 'whiteAlpha.300' },
                  }}
                />
                <Portal>
                  <MenuList bg="gray.800" borderColor="whiteAlpha.200" minW="280px" py={0} overflow="hidden" zIndex="tooltip">
                    <MenuItem
                      bg="gray.800"
                      _hover={{ bg: 'whiteAlpha.100' }}
                      onClick={() => navigate('submit')}
                      py={3}
                      px={4}
                    >
                      <HStack spacing={3}>
                        <Box color="purple.300">
                          <Icon type="SubmitCode" />
                        </Box>
                        <VStack alignItems="flex-start" spacing={0}>
                          <Text fontWeight="semibold">Code Editor</Text>
                          <Text fontSize="micro" opacity="secondary" fontWeight="regular">
                            Submit code written in the built-in editor
                          </Text>
                        </VStack>
                      </HStack>
                    </MenuItem>
                    <MenuItem
                      bg="gray.800"
                      _hover={{ bg: 'whiteAlpha.100' }}
                      onClick={() => navigate('submit?via=github')}
                      py={3}
                      px={4}
                      borderTop="1px solid"
                      borderColor="whiteAlpha.100"
                    >
                      <HStack spacing={3}>
                        <Box>
                          <Icon type="Github" size={20} />
                        </Box>
                        <VStack alignItems="flex-start" spacing={0}>
                          <Text fontWeight="semibold">Submit via GitHub</Text>
                          <Text fontSize="micro" opacity="secondary" fontWeight="regular">
                            Link your deployed website and repository
                          </Text>
                        </VStack>
                      </HStack>
                    </MenuItem>
                  </MenuList>
                </Portal>
              </Menu>
              </HStack>
            </HStack>
          ),
          rightSide: (
            <RightSideActions
              leftActions={
                !isSmallScreen ? (
                  <HStack spacing={2}>
                    <EditorSettingsButton />
                    <ShareButton />
                  </HStack>
                ) : undefined
              }
            />
          ),
        }}
        codeTabs={{
          html: formatCode(
            HTML_LEARN_TEMPLATE(
              `https://github.com/elise-bigdevsoon/big-dev-soon-${project.screen_name}`,
              project.title,
              'project'
            ),
            0
          ),
          css: '',
          js: '',
        }}
        callbacks={{
          onCodeFrameUrlChange: (codeFrameUrl) => {
            setCodeFrameUrl(codeFrameUrl);
          },
        }}
        paneTabs={{
          tabs: [
            {
              screenName: 'browser',
              title: 'Browser',
              icon: <Icon type="Browser" size={16} />,
              content: (
                <BrowserTab
                  browserUrl={browserUrl}
                  codeFrameUrl={codeFrameUrl}
                  onCodeFrameUrlChange={codeEditorRef.current?.onCodeFrameUrlChange}
                  designScreenshotUrl={project.design_screenshot.url}
                  showOverlayControls
                  overlayScreenshots={project.cards
                    .filter((c) => c.screenshot_url)
                    .map((c) => ({ url: c.screenshot_url!, title: c.title }))}
                  scoreConfig={{
                    designScreenshotUrl: project.design_screenshot.url,
                    screenshotWidth: 1440,
                    screenshotHeight: 1080,
                  }}
                />
              ),
            },
            {
              screenName: 'cards',
              title: `Cards (${completedCardsCount}/${project.cards.length})`,
              icon: <Icon type="CardsTab" size={16} />,
              content: (
                <VStack alignItems="flex-start" p={4} spacing={4}>
                  <VStack alignItems="flex-start" width="100%" spacing={4}>
                    <Tags tags={project.tags} />
                    <HStack justifyContent="space-between" alignItems="center" width="100%" pr={2}>
                      <VStack alignItems="flex-start">
                        <HStack>
                          <Text fontSize="xxLarge" fontWeight="bold">
                            {project.title}
                          </Text>
                          <ProjectElement
                            type={
                              project.status === User_Project_Status_Enum.Completed
                                ? 'experienceCompleted'
                                : 'experience'
                            }
                            experience={mapExpToProject[project.difficulty_lvl]}
                          />
                        </HStack>
                      </VStack>
                      <HStack>
                        {StatusIconMapper[project.status]}
                        <Text fontSize="small">{StatusTextMapper[project.status]}</Text>
                      </HStack>
                    </HStack>
                    <Text fontWeight="regular">{project.description}</Text>
                    <DifficultyMarker difficulty={project.difficulty_lvl} />
                  </VStack>
                  <Divider my={2} />
                  <VStack alignItems="flex-start" width="100%">
                    <HStack>
                      <Icon type="Cards" />
                      <Text fontSize="xLarge">Cards</Text>
                    </HStack>
                    <Text fontWeight="regular" opacity="secondary">
                      Organize and track your project tasks effectively using cards.
                    </Text>
                  </VStack>
                  <Box position="relative" width="100%">
                    {isFreeWithoutCards && authId && (
                      <Box
                        position="absolute"
                        left="50%"
                        top="30%"
                        transform="translate(-50%, -30%)"
                        minW="330px"
                      >
                        <VStack
                          spacing={4}
                          p={4}
                          borderRadius="md"
                          backgroundColor="gray.700"
                          boxShadow="0px 10px 20px 0px rgba(0, 0, 0, 0.15)"
                        >
                          <Text fontSize="large" fontWeight="semibold">
                            Upgrade plan to unlock all cards
                          </Text>
                          <Button
                            scheme="gradient"
                            to={ERoute.Plans}
                            leftIcon={<Icon type="Rocket" />}
                          >
                            Unlock BIG
                          </Button>
                        </VStack>
                      </Box>
                    )}
                    <VStack spacing={4} width="100%" alignItems="flex-start">
                      {project.cards.map((card, index) => {
                        const isFirstCard = index === 0;
                        const isCardAccessible = isFirstCard || !isFreeWithoutCards;

                        return (
                          <Box
                            key={card.id}
                            backgroundColor="whiteAlpha.200"
                            borderRadius={borderRadius}
                            p={4}
                            width="100%"
                            transition="all 0.2s ease-out"
                            cursor={isCardAccessible ? 'pointer' : 'default'}
                            _hover={isCardAccessible ? { backgroundColor: 'whiteAlpha.100' } : {}}
                            style={{
                              filter: isCardAccessible ? 'none' : 'blur(4px)',
                              pointerEvents: isCardAccessible ? 'auto' : 'none',
                            }}
                            onClick={() => isCardAccessible && handleCardClick(card)}
                            position="relative"
                          >
                            <HStack width="100%" justifyContent="space-between">
                              <HStack spacing={6}>
                                <CompleteCardCheckbox
                                  cardId={card.id}
                                  isCompleted={card.status === User_Card_Status_Enum.Completed}
                                  isProjectCompleted={
                                    project.status === User_Project_Status_Enum.Completed
                                  }
                                  projectScreenName={project.screen_name}
                                  cards={project.cards}
                                  userProjectId={project.user_project_id}
                                />
                                <HStack spacing={3}>
                                  <CardIcon type={card.type} size="md" />
                                  <Text fontSize="large">
                                    {index + 1}. {card.title}
                                  </Text>
                                </HStack>
                              </HStack>
                              <ChevronRightIcon boxSize={6} />
                            </HStack>
                          </Box>
                        );
                      })}
                    </VStack>
                  </Box>
                </VStack>
              ),
            },
            {
              screenName: 'discussions',
              title: `${
                project.discussion_count > 0
                  ? `Discussions (${project.discussion_count})`
                  : 'Discussions'
              }`,
              icon: <Icon type="Discussion" size={16} />,
              content: (
                <DiscussionTab
                  entityId={project.id}
                  type={Support_Type_Enum.Question}
                  refetch={() => {
                    queryClient.invalidateQueries({
                      queryKey: [TanstackQueryName.GetProject, project.screen_name],
                    });
                  }}
                />
              ),
            },
          ],
        }}
        footer={{
          assetsConfig: {
            showDesignTab: true,
            showExtractTextTab: true,
            showYourFilesTab: true,
            showPackagesTab: true,
            showToolsTab: false,
            designScreenshotUrl: project.design_screenshot.url,
            entityType: 'project',
            entityScreenName: project.screen_name,
            entityId: project.screen_name,
            projectCards: project.cards,
            isFreeUser: isFree,
            isDownloadingFigma: isDownloadingFigmaDesign,
            onFigmaDownload: () => {
              if (isFree) {
                figmaModal.onOpen();
                return;
              }
              onFigmaDownload();
            },
            githubLink: `https://github.com/elise-bigdevsoon/big-dev-soon-${project.screen_name}`,
          },
        }}
      />
      <CardDrawer
        project={project}
        currentCard={currentCard}
        isOpen={cardDrawer.isOpen}
        onClose={cardDrawer.onClose}
        onCurrentCardChange={setCurrentCard}
      />
      <UnlockFigmaModal
        isOpen={figmaModal.isOpen}
        onClose={figmaModal.onClose}
        isDownloading={isDownloadingFigmaDesign}
        onDownload={onFigmaDownload}
      />
    </>
  );
};

export default ProjectPlayground;

